import { Component, Inject, OnDestroy, OnInit, signal } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { MenuItem, PrimeIcons } from "primeng/api";
import { User, UserRoles } from "../user-admin-page/user-model";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { StorageKeys, StoreService } from "../../services/store.service";

@Component({
    selector: 'app-user-menu',
    template: `
        <button pButton pRipple type="button" class="p-button-rounded p-button-text p-text-secondary notification-button" icon="pi pi-user" title="User Menu" (click)="menu.toggle($event)"></button>
        <p-menu appendTo="body" #menu [popup]="true" [model]="items()"></p-menu>
    `,
    styles: [`
    button {
        background: transparent;
        border-color: transparent;
        }
    `],
    standalone: false
})
export class UserMenuComponent implements OnInit, OnDestroy {
    public items = signal<MenuItem[]>([]);
    private _destroy$ = new Subject<void>();

    constructor(
        @Inject(DOCUMENT)
        private _document: Document,
        private _auth: AuthService,
        private _storeService: StoreService
    ) { }

    ngOnInit(): void {
        this.initializeMenu();
    }

    private initializeMenu(): void {
        this._auth.user$
            .pipe(takeUntil(this._destroy$))
            .subscribe(async (u) => {
            const userRoles = await this._storeService.get<UserRoles>(StorageKeys.ROLES);
            const user = await this._storeService.get<User>(StorageKeys.USER);
            const accessToken = await firstValueFrom(this._auth.getAccessTokenSilently());

            if (u) {
                const menuItems: MenuItem[] = [{
                    label: u?.nickname?.toLowerCase() ?? "User Unknown",
                    items: [
                        {
                            label: 'User Profile',
                            icon: PrimeIcons.USER,
                            routerLink: '/profile'
                        },
                        {
                            label: 'Units',
                            icon: PrimeIcons.SERVER,
                            routerLink: '/profile/units'
                        },
                        {
                            label: 'Admin',
                            icon: PrimeIcons.USERS,
                            routerLink: '/users'
                        },
                        {
                            label: 'Hangfire',
                            icon: PrimeIcons.COG,
                            url: `${environment.PeriforEndpoint}/hangfire?token=${accessToken}`
                        },
                        {
                            label: 'Audit',
                            icon: PrimeIcons.COG,
                            routerLink: '/audit'
                        },
                        {
                            label: 'Logout',
                            icon: PrimeIcons.TIMES,
                            command: () => {
                                this.logout();
                            }
                        }
                    ]
                }];

                if (!userRoles?.admin && !user?.profile?.isGlobalAdmin) {
                    const adminIndex = menuItems[0]?.items?.findIndex(item => item.label === 'Admin');
                    if (adminIndex !== undefined && adminIndex > -1) {
                        menuItems[0]?.items?.splice(adminIndex, 1);
                    }
                }

                if (user?.profile?.organization !== 'Altus' || !user?.profile?.isGlobalAdmin) {
                    menuItems[0].items = menuItems[0].items.filter(
                    item => item.label !== 'Hangfire' && item.label !== 'Audit'
                    );
                }

                this.items.set(menuItems);
            } else {
                this.items.set([{
                    label: "Please Login",
                    items: [
                        {
                            label: 'Login',
                            icon: PrimeIcons.KEY,
                            command: () => {
                                this.login();
                            }
                        }
                    ]
                }]);
            }
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    login() {
        this._auth.loginWithRedirect({
            appState: {
                target: '/designs'
            }
        });
    }

    logout() {
        this._auth.logout({
            logoutParams: {
                returnTo: this._document.location.origin
            }
        });
    }
}
